/* Velura storefront enhancements: reveals, size & fit dialog, units, delivery estimate, sticky add to cart.
   Everything here is progressive: content is visible and usable without this file. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Reveal on scroll. Elements start visible unless JS adds .vl-js, and are never hidden for reduced motion. */
  function initReveal(root = document) {
    const items = root.querySelectorAll('[data-vl-reveal]:not(.is-in), [data-vl-split]:not(.is-in)');
    if (!items.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0 } // threshold 0: clipped (mask) elements report a 0 ratio while hidden
    );
    items.forEach((el) => io.observe(el));
  }


  /* Motion setup: tag elements for the reveal system before it starts observing.
     Hidden-until-revealed styles only apply under .vl-motion, which is added here,
     so if this script never runs everything simply stays visible. */
  function initMotion(root = document) {
    if (reduceMotion.matches) return;
    document.documentElement.classList.add('vl-motion');

    // Headlines rise word by word (text stays in the DOM in reading order).
    root.querySelectorAll('[data-vl-split]:not([data-vl-split-done]), .vl-band__title').forEach((el) => {
      if (el.dataset.vlSplitDone) return;
      el.dataset.vlSplitDone = '1';
      el.setAttribute('data-vl-split', '');
      const label = el.textContent.trim();
      const words = label.split(/\s+/);
      el.setAttribute('aria-label', label);
      el.innerHTML = words.map((w, i) => `<span class="vl-w" aria-hidden="true"><span class="vl-w__i" style="--i:${i}">${w.replace(/&/g, '&amp;').replace(/</g, '&lt;')}</span></span>`).join(' ');
    });

    // Tiles and product cards open like curtains, staggered across the row.
    const stagger = (selector, media) => {
      root.querySelectorAll(selector).forEach((list) => {
        [...list.children].forEach((item, i) => {
          const target = media ? item.querySelector(media) : item;
          if (!target || target.hasAttribute('data-vl-reveal')) return;
          target.setAttribute('data-vl-reveal', 'mask');
          target.style.setProperty('--vl-d', String(i % 4));
        });
      });
    };
    stagger('.vl-tiles__grid', '.vl-tile__media');
    stagger('.resource-list', '.card-gallery');
    root.querySelectorAll('.vl-tile__copy, .vl-band__sub, .product-card__content, .vl-mark, .vl-promise__item').forEach((el, i) => {
      if (el.hasAttribute('data-vl-reveal')) return;
      el.setAttribute('data-vl-reveal', el.classList.contains('vl-mark') ? 'mark' : '');
    });
  }


  /* Fashion film: fetch the video only when it nears the screen, play muted inline while visible,
     pause when it leaves. Reduced-motion visitors start paused; the button always works. */
  function initFilm(root = document) {
    root.querySelectorAll('[data-vl-film]').forEach((frame) => {
      if (frame.dataset.ready) return;
      frame.dataset.ready = '1';
      const video = frame.querySelector('video');
      const toggle = frame.querySelector('[data-vl-film-toggle]');
      if (!video) return;
      const label = toggle?.querySelector('.vl-film__label');
      let wanted = !reduceMotion.matches; // user intent: play when visible
      let loaded = false;
      const load = () => {
        if (loaded) return;
        loaded = true;
        video.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; });
        video.load();
      };
      const setState = (playing) => {
        frame.classList.toggle('is-playing', playing);
        toggle?.setAttribute('aria-pressed', String(playing));
        if (label) label.textContent = playing ? 'Pause' : 'Play';
      };
      const play = () => { load(); const p = video.play(); if (p && p.catch) p.catch(() => setState(false)); };
      video.addEventListener('playing', () => setState(true));
      video.addEventListener('pause', () => setState(false));
      toggle?.addEventListener('click', () => {
        wanted = video.paused;
        if (wanted) play(); else video.pause();
      });
      if (!('IntersectionObserver' in window)) { if (wanted) play(); return; }
      new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) { load(); if (wanted && video.paused) play(); }
        else if (!video.paused) { video.pause(); }
      }, { rootMargin: '200px 0px 200px 0px', threshold: 0 }).observe(frame);
    });
  }


  /* Smooth image arrival: images that are not yet loaded fade in instead of popping in.
     The hero is excluded so the largest image paints immediately. Content stays visible if JS fails. */
  function initImageFade(root = document) {
    root.querySelectorAll('.vl-section img:not(.vl-ch__img):not([data-vl-fade]), .product-card img:not([data-vl-fade])').forEach((img) => {
      img.setAttribute('data-vl-fade', '');
      if (img.complete && img.naturalWidth) return;
      img.classList.add('vl-img-wait');
      const done = () => img.classList.remove('vl-img-wait');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  /* Homepage picks join Horizon's card-to-product image transition (same attributes as its own cards). */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.vl-pick__link');
    if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey) return;
    const img = link.querySelector('.vl-pick__img:not(.vl-pick__img--alt)');
    if (!img) return;
    img.setAttribute('data-view-transition-type', 'product-image-transition');
    img.setAttribute('data-view-transition-triggered', 'true');
  });


  /* Hero video: fetched only after the page has loaded, so the still is the first paint.
     Fades in once it is actually playing. Skipped for reduced motion and Data Saver. */
  function initHeroVideo(root = document) {
    const v = root.querySelector('[data-vl-hero-video]');
    if (!v || v.dataset.ready) return;
    v.dataset.ready = '1';
    if (reduceMotion.matches || navigator.connection?.saveData) return;
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    const start = () => {
      v.querySelectorAll('source[data-src]').forEach((s) => { s.src = s.dataset.src; });
      v.load();
      // Reveal only once frames are actually advancing, so a slow start never shows a blank frame over the still.
      v.addEventListener('timeupdate', () => { if (v.currentTime > 0.08 && !v.ended) v.classList.add('is-playing'); });
      // Play the walk once, crossfade back to the still (the clip's own first frame), rest, then play again.
      v.addEventListener('ended', () => {
        v.classList.remove('is-playing');
        setTimeout(() => { if (document.hidden) return; v.currentTime = 0; v.play().catch(() => {}); }, desktop ? 1800 : 6500);
      });
      const p = v.play(); if (p && p.catch) p.catch(() => {});
    };
    const later = () => setTimeout(start, 400);
    if (document.readyState === 'complete') later(); else window.addEventListener('load', later, { once: true });
    document.addEventListener('visibilitychange', () => { if (document.hidden) v.pause(); else if (v.classList.contains('is-playing')) v.play().catch(() => {}); });
  }

  /* Dialogs (size & fit). Native <dialog> gives focus trapping and Escape handling. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-vl-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.vlDialogOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        dialog.showModal();
        document.documentElement.classList.add('vl-lock');
      }
      return;
    }
    const closer = event.target.closest('[data-vl-dialog-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('vl-dialog')) {
      event.target.close(); // backdrop click
    }
  });
  document.addEventListener(
    'close',
    (event) => {
      if (event.target.classList?.contains('vl-dialog')) document.documentElement.classList.remove('vl-lock');
    },
    true
  );

  /* Inches / centimeters toggle for measurement tables. Converts numeric cells only. */
  function setUnits(dialog, unit) {
    dialog.querySelectorAll('[data-vl-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.vlUnit === unit)));
    dialog.querySelectorAll('.vl-measure td:not(.vl-nc)').forEach((td) => {
      if (!td.dataset.in) td.dataset.in = td.textContent.trim();
      const src = td.dataset.in;
      td.textContent =
        unit === 'cm'
          ? src.replace(/\d+(\.\d+)?/g, (n) => String(Math.round(parseFloat(n) * 2.54)))
          : src;
    });
    dialog.querySelectorAll('.vl-measure thead th:first-child').forEach((th) => {
      if (/inch|velura/i.test(th.textContent) || th.dataset.unitHead) {
        if (!th.dataset.unitHead) th.dataset.unitHead = th.textContent;
        if (/inch/i.test(th.dataset.unitHead)) th.textContent = unit === 'cm' ? 'Centimeters' : 'Inches';
      }
    });
    dialog.querySelectorAll('.vl-measure--conv thead th').forEach((th) => {
      if (!th.dataset.label) th.dataset.label = th.textContent;
      th.textContent = th.dataset.label.replace('(in)', unit === 'cm' ? '(cm)' : '(in)');
    });
  }
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-vl-unit]');
    if (!btn) return;
    setUnits(btn.closest('dialog') || document, btn.dataset.vlUnit);
  });

  /* Delivery estimate: add business days to today, skipping weekends. */
  function addBusinessDays(date, days) {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  }
  function initDelivery(root = document) {
    const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
    root.querySelectorAll('[data-vl-delivery]').forEach((el) => {
      const min = parseInt(el.dataset.min, 10);
      const max = parseInt(el.dataset.max, 10);
      const out = el.querySelector('[data-vl-delivery-range]');
      if (!out || Number.isNaN(min) || Number.isNaN(max)) return;
      const now = new Date();
      out.textContent = `${fmt.format(addBusinessDays(now, min))} – ${fmt.format(addBusinessDays(now, max))}`;
      // Arrive-by cut-off: latest order day whose slowest delivery still lands on or before the deadline.
      const dl = el.querySelector('[data-vl-deadline]');
      if (dl) {
        const end = new Date(dl.dataset.vlDeadline + 'T23:59:59');
        let order = new Date(end);
        while (addBusinessDays(order, max) > end) order.setDate(order.getDate() - 1);
        while (order.getDay() === 0 || order.getDay() === 6) order.setDate(order.getDate() - 1);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        if (!Number.isNaN(end.getTime()) && order >= today) {
          const day = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(order);
          dl.textContent = `Order by ${day} to have it ${dl.dataset.label}.`;
          dl.hidden = false;
        }
      }
    });
  }

  /* Real sale countdown: shown only until the sale end time; hides itself once it has passed. */
  function initSale(root = document) {
    const els = root.querySelectorAll('[data-vl-sale-end]');
    if (!els.length) return;
    const tick = () => {
      const now = Date.now();
      els.forEach((el) => {
        const end = Date.parse(el.dataset.vlSaleEnd);
        if (Number.isNaN(end) || now >= end) { el.hidden = true; return; }
        el.hidden = false;
        const left = Math.floor((end - now) / 1000);
        const d = Math.floor(left / 86400), h = Math.floor((left % 86400) / 3600), m = Math.floor((left % 3600) / 60), sec = left % 60;
        el.querySelectorAll('[data-vl-countdown]').forEach((c) => {
          c.textContent = c.hasAttribute('data-short')
            ? (d > 0 ? `in ${d}d ${h}h` : `in ${h}h ${m}m`)
            : (d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m ${String(sec).padStart(2, '0')}s`);
        });
      });
    };
    tick();
    setInterval(tick, 1000);
  }

  /* "Only N left": real supplier stock (3 or fewer) for whichever size/colour is selected. */
  function initLowStock(root = document) {
    root.querySelectorAll('[data-vl-low]').forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      let map = {};
      try { map = JSON.parse(el.dataset.vlLow); } catch (e) { return; }
      const scope = el.closest('.shopify-section') || document;
      const current = () => scope.querySelector('form[action*="/cart/add"] [name="id"]')?.value || new URLSearchParams(location.search).get('variant');
      let last = null;
      const update = () => {
        const id = current();
        if (id === last) return;
        last = id;
        const n = map[id];
        el.hidden = !n;
        if (n) el.textContent = `Only ${n} left in this size and colour`;
      };
      update();
      scope.addEventListener('change', () => setTimeout(update, 60));
      document.addEventListener('variant:update', () => setTimeout(update, 60));
      setInterval(update, 800);
    });
  }

  /* Sticky add to cart on phones. */
  function initSticky(root = document) {
    root.querySelectorAll('[data-vl-sticky]').forEach((bar) => {
      if (bar.dataset.ready) return;
      bar.dataset.ready = '1';
      const scope = bar.closest('.shopify-section') || document;
      const form = scope.querySelector('product-form-component') || document.querySelector('product-form-component');
      const submit = form?.querySelector('button[type="submit"][name="add"]');
      if (!submit) return;
      document.body.appendChild(bar);
      bar.hidden = false;

      const io = new IntersectionObserver(([entry]) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-shown', passed);
        bar.setAttribute('aria-hidden', String(!passed));
        bar.querySelector('button').tabIndex = passed ? 0 : -1;
      });
      io.observe(submit);

      const btn = bar.querySelector('[data-vl-sticky-btn]');
      const sync = () => {
        btn.disabled = submit.disabled;
        const label = submit.textContent.trim();
        if (submit.disabled && label) btn.textContent = label;
        else btn.textContent = btn.dataset.label || (btn.dataset.label = btn.textContent);
        const price = scope.querySelector('product-price .price, product-price [ref="priceContainer"]');
        const out = bar.querySelector('[data-vl-sticky-price]');
        if (price && out) out.textContent = price.textContent.replace(/\s+/g, ' ').trim();
      };
      btn.dataset.label = btn.textContent;
      new MutationObserver(sync).observe(form, { subtree: true, attributes: true, childList: true, characterData: true });
      sync();
      btn.addEventListener('click', () => submit.click());
    });
  }

  /* Pause decorative hero video for reduced-motion users. */
  function initVideo(root = document) {
    if (!reduceMotion.matches) return;
    root.querySelectorAll('.vl-hero__video video, video.vl-hero__video, .vl-banner__media video').forEach((v) => {
      v.pause();
      v.removeAttribute('autoplay');
    });
  }


  /* Email pop-up: once per visitor, after a delay, never while another dialog is open. */
  function initPopup(root = document) {
    root.querySelectorAll('[data-vl-pop]').forEach((dialog) => {
      let seen = false;
      try { seen = localStorage.getItem('vl-pop-seen') === '1'; } catch (e) {}
      const posted = dialog.querySelector('[role="status"]');
      if (posted) { dialog.showModal?.(); return; }
      if (seen || typeof dialog.showModal !== 'function') return;
      const delay = (parseInt(dialog.dataset.delay, 10) || 12) * 1000;
      setTimeout(() => {
        if (document.querySelector('dialog[open]')) return;
        dialog.showModal();
        try { localStorage.setItem('vl-pop-seen', '1'); } catch (e) {}
      }, delay);
    });
  }

  function init(root) {
    document.documentElement.classList.add('vl-js');
    initMotion(root);
    initReveal(root);
    initFilm(root);
    initImageFade(root);
    initHeroVideo(root);
    initDelivery(root);
    initSale(root);
    initLowStock(root);
    initSticky(root);
    initVideo(root);
    initPopup(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
